import {
  type PlatformRole,
  type UserStatus,
  type DocumentVersionStatus,
} from "../config/types";

export const sessionStatusParam = "session";
export const sessionStatusExpired = "expired";

export class FetchError extends Error {
  code: string;
  details?: unknown;

  constructor(code: string, message?: string, details?: unknown) {
    super(message);
    this.code = code;
    this.details = details;
  }
}

export interface ApiError {
  code: string;
  details?: Record<string, unknown>;
  message?: string;
}

export type ScheduleReviewPeriod = "3 MONTH" | "6 MONTH" | "1 YEAR" | null;

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName?: string;
  imageUrl?: string;
  imageEtag?: string;
  position?: string | null;
  role: PlatformRole;
  status: UserStatus;
  categories: {
    id: number;
    title: string;
  }[];
}

export interface CompanyGroup {
  id: number;
  name: string;
  slug: string;
  parentCompanyGroupId: number | null;
  logoUrl: string | null;
  isAncestor: boolean;
  isActive: boolean;
  isHome: boolean;
  canSwitch: boolean;
}

export interface CreateCompanyGroupData {
  name: string;
  parentCompanyGroupId: number;
  superadmin: {
    email: string;
    firstName: string;
    lastName: string;
    position?: string;
  };
}

export interface CurrentUser extends User {
  isSsoEnabled: boolean;
  isTwoFaEnabled: boolean;
  isUsingSpe: boolean;
}

export interface SsoDetails {
  redirectUrl: string;
}

export interface CreateSessionResponse {
  user: CurrentUser;
  ssoActivated: boolean;
  ssoAvailable: boolean;
}

export interface OfficeData {
  title: string;
  accessToken: string;
  accessTokenTtl: string;
  officeActionUrl: string;
  faviconUrl: string;
  userId: string;
  ownerId: string;
  size: number;
  fileType: FileType;
  isReadOnly: boolean;
}

export type FileType =
  | "docx"
  | "xlsx"
  | "pptx"
  | "wopitest"
  | "wopitestx"
  | "pdf";

export interface DocumentVersionSummary {
  id: number;
  title: string;
  status: DocumentVersionStatus;
  fileType: FileType;
  reviewFrom: string | null;
  reviewTo: string | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  fileUpdatedAt: string | null;
  readers: ReaderCountSummary;
  editorIds: number[];
  reviewerIds: number[];
  document: DocumentVersionSummaryDocument;
  permissions: {
    canPublish: boolean;
  };
  isOngoing?: boolean;
  steps?: WorkflowStep[];
  totalSteps?: number;
  completedSteps?: number;
  workflowTemplateId?: number | null;
  workflowTemplateName?: string | null;
}

interface DocumentVersionSummaryDocument {
  id: number;
  name: string;
  referenceNumber: string | null;
  tier: TierSummary | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tagIds: number[];
  parentDocumentIds: number[];
  childDocumentIds: number[];
  scheduleReviewPeriod: ScheduleReviewPeriod;
  chartOrder: number | null;
}

interface ReaderCountSummary {
  totalCount: number;
  openedCount: number;
  readCount: number;
  currentUser?: {
    hasOpened: boolean;
    hasAccepted: boolean;
  };
}

interface AuditTrailEntry {
  id: number;
  title: string;
  color: string;
  createdAt: string;
  user: UserSummary;
  anchoredUser: string;
}

export interface DocumentVersionDetails {
  id: number;
  title: string;
  status: DocumentVersionStatus;
  fileId: string;
  fileType: FileType;
  reviewFrom: string | null;
  reviewTo: string | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  fileUpdatedAt: string | null;
  createdBy: UserSummary;
  reviewers: UserSummary[];
  editors: UserSummary[];
  readers: ReaderSummary[];
  auditTrail: AuditTrailEntry[];
  permissions: VersionPermissions;
  description: string | null;
  isOngoing: boolean;
  workflowTemplateId: number | null;
  workflowTemplateName: string | null;
  steps: WorkflowStep[];
  stepInstances: WorkflowStepInstance[];
  document: {
    id: number;
    referenceNumber: string | null;
    tier: TierSummary | null;
    category: IdAndTitle;
    owner: UserSummary;
    tags: IdAndTitle[];
    parentDocuments: IdAndTitle[];
    childDocuments: IdAndTitle[];
    versions: OtherVersionSummary[];
    scheduleReviewPeriod: ScheduleReviewPeriod;
  };
}

export interface IdAndTitle {
  id: number;
  title: string;
}

export interface TagWithDocuments extends IdAndTitle {
  documents?: {
    id: number;
    documentVersions: {
      id: number;
      title: string;
      status: DocumentVersionStatus;
    }[];
    archivedAt: string | null;
  }[];
}

interface TierSummary extends IdAndTitle {
  parentId: number | null;
}

export interface TierDetails extends TierSummary {
  description: string;
}

interface OtherVersionSummary extends IdAndTitle {
  status: DocumentVersionStatus;
  publishedAt: string | null;
}

export interface ReaderSummary {
  userId: number;
  email: string;
  firstName: string;
  lastName?: string;
  hasOpened?: boolean;
  hasRead?: boolean;
}

interface UserSummary {
  userId: number;
  firstName: string;
  lastName?: string;
  email: string;
  imageUrl?: string;
  hasOpened?: boolean;
}

export interface CreateUserData {
  email: string;
  position?: string | null;
  role: string;
  firstName: string;
  lastName: string;
  categoryIds?: (number | string)[];
}

export interface UpdateUserData extends Partial<CreateUserData> {
  file?: File;
  status?: UserStatus;
}

export const WorkflowStepUserPermissions = {
  read: "read",
  write: "write",
} as const;

export type WorkflowStepUserPermission =
  (typeof WorkflowStepUserPermissions)[keyof typeof WorkflowStepUserPermissions];

export const WorkflowStepUserResponses = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
} as const;

export type WorkflowStepUserResponse =
  (typeof WorkflowStepUserResponses)[keyof typeof WorkflowStepUserResponses];

export const WorkflowStepInstanceCommentTypes = {
  free: "free",
  approved: "approved",
  rejected: "rejected",
} as const;

export type WorkflowStepInstanceCommentType =
  (typeof WorkflowStepInstanceCommentTypes)[keyof typeof WorkflowStepInstanceCommentTypes];

export interface WorkflowStepUser {
  userId: number;
  firstName: string;
  lastName?: string;
  imageUrl?: string;
  role?: PlatformRole;
  permission: WorkflowStepUserPermission;
  responseStatus: WorkflowStepUserResponse;
}

type WorkflowTemplateStepUser = Pick<WorkflowStepUser, "userId" | "permission">;

export const WorkflowStepStatuses = {
  completed: "completed",
  active: "active",
  upcoming: "upcoming",
} as const;
export type WorkflowStepStatus =
  (typeof WorkflowStepStatuses)[keyof typeof WorkflowStepStatuses];

export type WorkflowStepType = "review" | "approval";

interface WorkflowStepBase {
  id: number;
  name: string;
  type: WorkflowStepType;
  order: number;
}

export interface WorkflowStep extends WorkflowStepBase {
  description: string;
  dueAt: Date | null;
  status: WorkflowStepStatus;
  users: WorkflowStepUser[];
}

interface WorkflowTemplateStep extends WorkflowStepBase {
  description: string | null;
  dueAt: string | null;
  users: WorkflowTemplateStepUser[];
}

export interface WorkflowStepInstanceComment {
  id: number;
  createdBy: UserSummary;
  createdAt: string;
  type: WorkflowStepInstanceCommentType;
  text: string | null;
}

export interface WorkflowStepInstance {
  id: number;
  stepId: number;
  createdBy: UserSummary;
  createdAt: string;
  completedAt: string | null;
  isLatestStepInstance: boolean;
  comments: WorkflowStepInstanceComment[];
}

export type WorkflowStepInput = Pick<
  WorkflowStep,
  "name" | "description" | "type" | "order" | "status" | "users" | "dueAt"
> & {
  id?: number; // New steps don't have an ID
  _key: string; // Client-side stable key for React reconciliation
};

export type OfficeFileType = Exclude<
  FileType,
  "wopitest" | "wopitestx" | "pdf"
>;

export type OfficeVersion = DocumentDetailsVersion & {
  fileType: OfficeFileType;
};

export const isOfficeVersion = (
  version: DocumentDetailsVersion,
): version is OfficeVersion => {
  return ["docx", "xlsx", "pptx"].includes(version.fileType);
};

interface VersionPermissions {
  canRead: boolean;
  canWrite: boolean;
  canManage: boolean;
  canPublish: boolean;
}

export interface DocumentDetailsVersion {
  id: number;
  title: string;
  status: DocumentVersionStatus;
  fileId: string;
  fileType: FileType;
  reviewFrom: string | null;
  reviewTo: string | null;
  isWorkflowOngoing: boolean;
  fileUpdatedAt: string | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
  lockedBy: UserSummary | null;
  permissions: VersionPermissions;
}

export interface DocumentDetails {
  id: number;
  name: string;
  projectId: string;
  referenceNumber: string | null;
  tier: IdAndTitle | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tags: IdAndTitle[];
  parentDocuments: IdAndTitle[];
  childDocuments: IdAndTitle[];
  versions: DocumentDetailsVersion[];
  scheduleReviewPeriod: ScheduleReviewPeriod;
  archivedAt: string | null;
}

export interface DocumentSummaryVersion {
  id: number;
  title: string;
  status: DocumentVersionStatus;
  fileType: FileType;
  isWorkflowOngoing: boolean;
  reviewFrom: string | null;
  reviewTo: string | null;
  fileUpdatedAt: string | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  createdAt: string;
}

export interface DocumentSummary {
  id: number;
  name: string;
  referenceNumber: string | null;
  tier: IdAndTitle | null;
  category: IdAndTitle;
  owner: UserSummary;
  publisher: UserSummary | null;
  tags: IdAndTitle[];
  parentDocumentIds: number[];
  childDocumentIds: number[];
  sharedToCompanyGroupIds: number[];
  chartOrder: number | null;
  scheduleReviewPeriod: ScheduleReviewPeriod;
  archivedAt: string | null;
  latestVersion: DocumentSummaryVersion;
  latestPublishedVersion: DocumentSummaryVersion | null;
  pin?: {
    description: string | null;
    createdAt: string;
  };
  /** Set on synthetic rows for documents shared to this group by an ancestor. */
  sharedBy?: IdAndName;
}

export interface DocumentHistoryEvent {
  timestamp: string;
  type: DocumentEventType;
  details?: Record<string, unknown>;
  version?: {
    id: number;
    order: number;
  };
  actor?: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    imageUrl?: string;
  };
}

export interface DocumentHistoryResponse {
  history: DocumentHistoryEvent[];
}

type DocumentEventType =
  | "version_created"
  | "version_published"
  | "version_adopted"
  | "version_archived"
  | "document_user_added"
  | "document_user_removed"
  | "workflow_step_user_added"
  | "workflow_step_user_removed"
  | "workflow_started"
  | "workflow_transition"
  | "workflow_completed";

interface ReaderReportSummary {
  total: number;
  opened: number;
  read: number;
}

export interface ReaderReport {
  readers: ReaderSummary[];
  summary: ReaderReportSummary;
}
export interface CategoryData {
  title: string;
  description?: string | undefined;
}

export interface CategoryDetails {
  id: number;
  title: string;
  description: string;
  details: {
    documents: number;
  };
}

export interface CategoryType {
  title: string;
  description: string;
  id: number;
  order: number;
}

export const LanguageCodeMap = {
  BG: "BG",
  CS: "CS",
  DA: "DA",
  DE: "DE",
  EL: "EL",
  EN_GB: "en-GB",
  EN_US: "en-US",
  ES: "ES",
  ET: "ET",
  FI: "FI",
  FR: "FR",
  HU: "HU",
  ID: "ID",
  IT: "IT",
  JA: "JA",
  KO: "KO",
  LT: "LT",
  LV: "LV",
  NB: "NB",
  NL: "NL",
  PL: "PL",
  PT: "PT",
  RO: "RO",
  RU: "RU",
  SK: "SK",
  SL: "SL",
  SV: "SV",
  TR: "TR",
  UK: "UK",
  ZH: "ZH",
  AUTO_DETECT: "AUTO_DETECT",
} as const;

export type LanguageCode =
  (typeof LanguageCodeMap)[keyof typeof LanguageCodeMap];

export interface SpSite {
  id: string;
  displayName: string;
  webUrl: string;
}

export interface SpDrive {
  id: string;
  name: string;
  driveType: string;
  webUrl: string;
}

export interface VerifySpAccessResponse {
  ok: boolean;
}

interface SectionVersionDocumentUsage {
  id: number;
  documentId: number;
  title: string;
  status: DocumentVersionStatus;
}

export interface SectionVersion {
  id: number;
  htmlContent: string;
  xmlContent: string;
  createdAt: string;
  createdBy: UserSummary;
  usedInDocumentVersions: Array<SectionVersionDocumentUsage>;
}

export interface Section {
  id: number;
  name: string;
  sectionVersions: SectionVersion[];
}

export interface WorkflowTemplateListItem {
  id: number;
  isDefault: boolean;
  name: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  stepCount: number;
}

export interface WorkflowTemplate {
  id: number;
  isDefault: boolean;
  name: string | null;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  steps: WorkflowTemplateStep[];
}

export interface WorkflowTemplateData {
  name: string | null;
  description: string | null;
  steps: {
    id?: number;
    name: string;
    description: string | null;
    type: WorkflowStepType;
    dueAt: Date | null;
    users: WorkflowTemplateStepUser[];
  }[];
}

export interface ApplyDueDateRow {
  reviewFrom: string | null;
  reviewTo: string | null;
  stepDueDates: (string | null)[];
}

/* Document delegation (shared documents) */

export interface DocumentShareTarget {
  companyGroupId: number;
  name: string;
  sharedAt: string;
}

interface SharedDocumentVersion {
  id: number;
  title: string;
  status: DocumentVersionStatus;
  fileType: FileType;
  hasPdfRendition: boolean;
  publishedAt: string | null;
  archivedAt: string | null;
  createdAt: string;
}

interface SharedDocumentAdoption {
  adoptedAt: string;
  adoptedBy: { userId: number; firstName: string; lastName?: string } | null;
}

export interface SharedDocumentSummary {
  documentId: number;
  sharedBy: IdAndName;
  sharedAt: string;
  latestPublishedVersion: SharedDocumentVersion;
  adoption: SharedDocumentAdoption | null;
  readers: ReaderCountSummary;
}

export interface SharedDocumentDetailsVersion extends SharedDocumentVersion {
  adoption: SharedDocumentAdoption | null;
  readers: ReaderSummary[];
  readerSummary: ReaderCountSummary;
}

export interface SharedDocumentDetails {
  documentId: number;
  sharedBy: IdAndName;
  sharedAt: string;
  latestPublishedVersionId: number;
  versions: SharedDocumentDetailsVersion[];
}

interface IdAndName {
  id: number;
  name: string;
}

export interface SharedDocumentsOverviewRow {
  documentId: number;
  title: string;
  group: IdAndName;
  latestPublishedVersion: SharedDocumentVersion;
  adoption: SharedDocumentAdoption | null;
  readers: {
    invitedCount: number;
    openedCount: number;
    readCount: number;
  };
}

export interface SharedDocumentsOverview {
  sharedTo: SharedDocumentsOverviewRow[];
  sharedBy: SharedDocumentsOverviewRow[];
}

interface QuizCreatedBy {
  id: number;
  name: string | null;
  surname: string | null;
}

/** Whether a quiz's questions are still being generated or ready to take. */
export type QuizStatus = "generating" | "ready";

/** A quiz on a published document version, with invited/completed taker counts. */
export interface QuizSummary {
  id: number;
  documentVersionId: number;
  documentId: number;
  title: string;
  publishedAt: string | null;
  status: QuizStatus;
  createdBy: QuizCreatedBy | null;
  createdAt: string;
  invitedCount: number;
  completedCount: number;
}

/** A quiz the current user is invited to, with their completion status. */
export interface MyQuiz {
  id: number;
  documentVersionId: number;
  documentId: number;
  title: string;
  publishedAt: string | null;
  createdBy: QuizCreatedBy | null;
  createdAt: string;
  completed: boolean;
}

export interface QuizAnswerOption {
  text: string;
  isCorrect: boolean;
}

interface QuizQuestion {
  id: number;
  question: string;
  options: QuizAnswerOption[];
  /** Why the correct answer is correct; null when none. Shown after a correct answer. */
  reason: string | null;
}

/** A full quiz with its questions and answer options — for taking or editing. */
export interface QuizDetail {
  id: number;
  documentVersionId: number;
  documentId: number;
  title: string;
  publishedAt: string | null;
  createdBy: QuizCreatedBy | null;
  createdAt: string;
  invited: boolean;
  completed: boolean;
  questions: QuizQuestion[];
}

interface QuizTaker {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  completed: boolean;
}

/** Invited/completed report for one quiz. */
export interface QuizTakersReport {
  takers: QuizTaker[];
  summary: { invited: number; completed: number };
}
